import { useEffect, useState } from "react";

interface Person {
  name: string;
  role: string;
  company: string;
  country: string;
  flag: string;
  profileImage: string;
  testimonial: string;
  x: number;
  y: number;
}

interface FloatingComment {
  id: string;
  person: Person;
  progress: number;
  startTime: number;
  isVisible: boolean;
}

interface ConnectionLine {
  id: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  progress: number;
  duration: number;
  startTime: number;
}

const people: Person[] = [
  {
    name: "Olatunde",
    role: "Software Engineer",
    company: "Skills Technologies",
    country: "Nigeria",
    flag: "🇳🇬",
    profileImage: "/images/olatunde.jpeg",
    testimonial:
      "Qicino Limited delivered our website ahead of schedule. Their software development expertise is outstanding!",
    x: 52, // Nigeria position as percentage
    y: 65,
  },
  {
    name: "Sarah Mitchell",
    role: "VP of Engineering",
    company: "TechCorp USA",
    country: "United States",
    flag: "🇺🇸",
    profileImage: "/images/profile-2.jpg",
    testimonial:
      "Exceptional cloud infrastructure migration. 24/7 support made all the difference!",
    x: 25,
    y: 45,
  },
  {
    name: "James Thompson",
    role: "Lead Developer",
    company: "Melbourne Innovations",
    country: "Australia",
    flag: "🇦🇺",
    profileImage: "/images/profile-3.jpg",
    testimonial:
      "UI/UX design completely revolutionized our user experience. 300% conversion increase!",
    x: 85,
    y: 80,
  },
  {
    name: "Emma Watson",
    role: "Digital Director",
    company: "London Enterprises",
    country: "United Kingdom",
    flag: "🇬🇧",
    profileImage: "/images/profile-4.jpg",
    testimonial:
      "Outstanding web development! E-commerce platform launched 3 months early.",
    x: 48,
    y: 35,
  },
  {
    name: "Michael Chen",
    role: "Senior Architect",
    company: "Toronto AI Labs",
    country: "Canada",
    flag: "🇨🇦",
    profileImage: "/images/profile-5.jpg",
    testimonial:
      "AI integration services accelerated our ML deployment by 6 months!",
    x: 22,
    y: 30,
  },
  {
    name: "Ayşe Demir",
    role: "Product Manager",
    company: "Istanbul Digital",
    country: "Turkey",
    flag: "🇹🇷",
    profileImage: "/images/profile-6.jpg",
    testimonial:
      "Cloud training program upskilled our entire development team. Outstanding!",
    x: 58,
    y: 42,
  },
];

export default function InteractiveWorldMap() {
  const [hoveredPerson, setHoveredPerson] = useState<Person | null>(null);
  const [activeComment, setActiveComment] = useState<FloatingComment | null>(
    null,
  );
  const [floatingArrow, setFloatingArrow] = useState<{
    x: number;
    y: number;
    targetPerson: Person;
  } | null>(null);
  const [connectionLines, setConnectionLines] = useState<ConnectionLine[]>([]);

  useEffect(() => {
    const createConnectionLine = () => {
      const fromPerson = people[Math.floor(Math.random() * people.length)];
      const toPerson = people[Math.floor(Math.random() * people.length)];

      if (fromPerson !== toPerson) {
        const connection: ConnectionLine = {
          id: Date.now().toString(),
          from: { x: fromPerson.x, y: fromPerson.y },
          to: { x: toPerson.x, y: toPerson.y },
          progress: 0,
          duration: 4000,
          startTime: Date.now(),
        };

        setConnectionLines((prev) => [...prev, connection]);

        // Remove the connection after animation completes
        setTimeout(() => {
          setConnectionLines((prev) =>
            prev.filter((conn) => conn.id !== connection.id),
          );
        }, connection.duration + 1000);
      }
    };

    const createFloatingComment = () => {
      const randomPerson = people[Math.floor(Math.random() * people.length)];

      // Start arrow from random position and float to the person's country
      const startX = Math.random() * 100;
      const startY = Math.random() * 100;

      setFloatingArrow({ x: startX, y: startY, targetPerson: randomPerson });

      // Animate arrow movement
      const duration = 3000;
      const startTime = Date.now();

      const animateArrow = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        if (progress < 1) {
          const currentX = startX + (randomPerson.x - startX) * progress;
          const currentY = startY + (randomPerson.y - startY) * progress;
          setFloatingArrow({
            x: currentX,
            y: currentY,
            targetPerson: randomPerson,
          });
          requestAnimationFrame(animateArrow);
        } else {
          // Arrow reached destination - show comment
          setFloatingArrow(null);
          const comment: FloatingComment = {
            id: Date.now().toString(),
            person: randomPerson,
            progress: 0,
            startTime: Date.now(),
            isVisible: true,
          };
          setActiveComment(comment);

          // Hide comment after 4 seconds
          setTimeout(() => {
            setActiveComment(null);
          }, 4000);
        }
      };

      requestAnimationFrame(animateArrow);
    };

    // Create connections and comments
    const connectionInterval = setInterval(createConnectionLine, 3000);
    const commentInterval = setInterval(createFloatingComment, 8000);

    // Start immediately
    createConnectionLine();
    createFloatingComment();

    return () => {
      clearInterval(connectionInterval);
      clearInterval(commentInterval);
    };
  }, []);

  return (
    <div className="relative w-full h-full min-h-[300px] md:min-h-[500px] flex items-center justify-center">
      {/* Real World Map */}
      <div className="relative w-full max-w-[90vw] md:max-w-[500px] aspect-square">
        {/* World Map with Real Earth Image */}
        <div className="relative w-full h-full rounded-full overflow-hidden shadow-2xl border-4 border-gradient-to-r from-green-400 to-orange-400">
          {/* Real Earth background using CSS to create realistic appearance */}
          <div
            className="w-full h-full bg-cover bg-center bg-no-repeat relative"
            style={{
              backgroundImage: `
                url("data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3cradialGradient id='ocean' cx='50%25' cy='50%25' r='50%25'%3e%3cstop offset='0%25' style='stop-color:%23257de8;stop-opacity:1' /%3e%3cstop offset='70%25' style='stop-color:%231e40af;stop-opacity:1' /%3e%3cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3e%3c/radialGradient%3e%3c/defs%3e%3ccircle cx='50' cy='50' r='50' fill='url(%23ocean)' /%3e%3c/svg%3e")
              `,
            }}
          >
            {/* Realistic continents with accurate geographical borders */}
            <svg
              viewBox="0 0 100 100"
              className="absolute inset-0 w-full h-full"
            >
              <defs>
                <radialGradient id="landGrad" cx="40%" cy="40%" r="60%">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.9} />
                  <stop offset="50%" stopColor="#16a34a" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#15803d" stopOpacity={1} />
                </radialGradient>
              </defs>

              {/* North America - More accurate shape based on real geography */}
              <path
                d="M 8 25 Q 5 18 12 16 Q 18 14 26 16 Q 32 18 35 22 Q 38 28 37 35 Q 35 42 31 48 Q 26 52 20 50 Q 14 48 10 42 Q 7 35 8 30 Q 8 27 8 25 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.3}
              />

              {/* Alaska */}
              <path
                d="M 5 28 Q 7 26 9 28 Q 8 30 6 29 Q 5 28 5 28 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.2}
              />

              {/* Greenland - Large realistic shape */}
              <path
                d="M 30 8 Q 34 6 38 10 Q 40 14 37 18 Q 34 19 30 17 Q 28 13 30 8 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.2}
              />

              {/* Great Lakes region */}
              <ellipse cx="25" cy="32" rx="2" ry="1" fill="#257de8" opacity={0.7} />
              <ellipse cx="27" cy="30" rx="1.5" ry="0.8" fill="#257de8" opacity={0.7} />

              {/* South America - Realistic shape */}
              <path
                d="M 24 52 Q 21 48 26 49 Q 30 51 32 56 Q 34 62 33 68 Q 32 74 30 78 Q 28 82 25 80 Q 22 76 21 70 Q 20 64 22 58 Q 23 55 24 52 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.3}
              />

              {/* Amazon River system */}
              <path
                d="M 24 60 Q 28 58 31 60 M 26 62 Q 29 61 32 62"
                stroke="#257de8"
                strokeWidth={0.3}
                fill="none"
                opacity={0.6}
              />

              {/* Europe - Realistic Mediterranean coastline */}
              <path
                d="M 45 18 Q 48 16 52 18 Q 56 20 58 24 Q 57 28 54 30 Q 50 31 46 29 Q 43 26 44 22 Q 45 20 45 18 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.3}
              />

              {/* Scandinavia - Norway, Sweden, Finland realistic shape */}
              <path
                d="M 50 12 Q 52 10 54 12 Q 55 16 53 18 Q 51 17 50 15 Q 49 13 50 12 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.2}
              />

              {/* British Isles - Great Britain and Ireland */}
              <path
                d="M 41 20 Q 43 18 45 21 Q 44 23 42 22 Q 40 21 41 20 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.2}
              />
              <circle cx="40" cy="21" r="0.8" fill="url(#landGrad)" /> {/* Ireland */}

              {/* Mediterranean Sea */}
              <ellipse
                cx="50"
                cy="32"
                rx="4"
                ry="1.5"
                fill="#257de8"
                opacity={0.5}
              />

              {/* Africa - Realistic continental shape */}
              <path
                d="M 47 32 Q 44 28 49 29 Q 54 31 57 36 Q 60 42 61 50 Q 62 58 60 66 Q 57 72 53 75 Q 48 76 44 72 Q 41 66 42 58 Q 43 50 45 42 Q 46 37 47 32 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.3}
              />

              {/* Madagascar - Realistic island shape */}
              <ellipse cx="58" cy="72" rx="1" ry="2" fill="url(#landGrad)" />

              {/* Nile River */}
              <path
                d="M 54 38 Q 55 45 56 52 Q 55 58 54 64"
                stroke="#257de8"
                strokeWidth={0.2}
                fill="none"
                opacity={0.6}
              />

              {/* Asia - Massive realistic continent */}
              <path
                d="M 58 12 Q 62 10 68 12 Q 74 15 80 18 Q 86 22 88 28 Q 87 34 84 40 Q 80 45 74 47 Q 68 46 62 44 Q 58 40 57 34 Q 56 28 57 22 Q 58 17 58 12 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.3}
              />

              {/* Himalayas - Mountain range with peaks */}
              <path
                d="M 65 35 L 67 33 L 69 35 L 71 33 L 73 35 L 75 33 L 77 35"
                stroke="#8B7355"
                strokeWidth={0.4}
                fill="none"
              />

              {/* Major Asian rivers */}
              <path
                d="M 70 30 Q 74 32 78 30 M 68 38 Q 72 36 76 38"
                stroke="#257de8"
                strokeWidth={0.2}
                fill="none"
                opacity={0.6}
              />

              {/* Japan - Realistic island chain */}
              <ellipse cx="85" cy="55" rx="1.5" ry="2" fill="url(#landGrad)" />
              <ellipse cx="87" cy="56" rx="0.8" ry="1.2" fill="url(#landGrad)" />

              {/* Southeast Asia - Philippines, Indonesia realistic archipelago */}
              <circle cx="78" cy="60" r="0.8" fill="url(#landGrad)" />
              <circle cx="75" cy="62" r="0.6" fill="url(#landGrad)" />
              <circle cx="76" cy="64" r="0.4" fill="url(#landGrad)" />
              <ellipse cx="80" cy="63" rx="1.2" ry="0.8" fill="url(#landGrad)" />

              {/* Australia - Realistic continental outline */}
              <path
                d="M 74 68 Q 77 66 82 68 Q 87 70 89 74 Q 88 78 85 80 Q 81 82 76 80 Q 72 78 71 74 Q 72 71 74 68 Z"
                fill="url(#landGrad)"
                stroke="#15803d"
                strokeWidth={0.3}
              />

              {/* Tasmania - Realistic island shape */}
              <ellipse cx="84" cy="82" rx="1" ry="0.8" fill="url(#landGrad)" />

              {/* New Zealand - Two main islands */}
              <ellipse cx="92" cy="82" rx="0.5" ry="1.5" fill="url(#landGrad)" />
              <ellipse cx="93" cy="85" rx="0.4" ry="1" fill="url(#landGrad)" />

              {/* Realistic grid lines */}
              {Array.from({ length: 6 }, (_, i) => (
                <line
                  key={`lat-${i}`}
                  x1={10}
                  y1={20 + i * 12}
                  x2={90}
                  y2={20 + i * 12}
                  stroke="rgba(34, 197, 94, 0.15)"
                  strokeWidth={0.2}
                />
              ))}
              {Array.from({ length: 8 }, (_, i) => (
                <line
                  key={`lng-${i}`}
                  x1={15 + i * 10}
                  y1={10}
                  x2={15 + i * 10}
                  y2={90}
                  stroke="rgba(249, 115, 22, 0.15)"
                  strokeWidth={0.2}
                />
              ))}
              
              {/* Connection lines between countries */}
              {connectionLines.map((connection) => {
                const elapsed = Date.now() - connection.startTime;
                const progress = Math.min(elapsed / connection.duration, 1);

                // Calculate intermediate points for curved path
                const midX =
                  (connection.from.x + connection.to.x) / 2 +
                  Math.sin(progress * Math.PI) * 8;
                const midY =
                  (connection.from.y + connection.to.y) / 2 -
                  Math.sin(progress * Math.PI) * 12;

                const pathLength =
                  Math.sqrt(
                    Math.pow(connection.to.x - connection.from.x, 2) +
                      Math.pow(connection.to.y - connection.from.y, 2),
                  ) * 1.5;

                const dashOffset = pathLength * (1 - progress);

                return (
                  <g key={connection.id}>
                    {/* Curved dotted line */}
                    <path
                      d={`M ${connection.from.x} ${connection.from.y} Q ${midX} ${midY} ${connection.to.x} ${connection.to.y}`}
                      fill="none"
                      stroke="#f97316"
                      strokeWidth={0.4}
                      strokeDasharray="1 1"
                      strokeDashoffset={dashOffset}
                      opacity={0.8}
                      className="animate-pulse"
                    />

                    {/* Signal pulse */}
                    {progress > 0.2 && (
                      <circle
                        cx={
                          connection.from.x +
                          (connection.to.x - connection.from.x) *
                            Math.max(0, progress - 0.2)
                        }
                        cy={
                          connection.from.y +
                          (connection.to.y - connection.from.y) *
                            Math.max(0, progress - 0.2)
                        }
                        r="0.8"
                        fill="#22c55e"
                        opacity={1 - progress}
                        className="animate-ping"
                      />
                    )}
                  </g>
                );
              })}
              
              {/* Floating arrow */}
              {floatingArrow && (
                <g>
                  <circle
                    cx={floatingArrow.x}
                    cy={floatingArrow.y}
                    r="1.2"
                    fill="#f97316"
                    className="animate-pulse"
                  />
                  <circle
                    cx={floatingArrow.x}
                    cy={floatingArrow.y}
                    r="2.5"
                    fill="none"
                    stroke="#f97316"
                    strokeWidth={0.3}
                    opacity={0.6}
                    className="animate-ping"
                  />
                  <polygon
                    points={`${floatingArrow.x + 2},${floatingArrow.y} ${floatingArrow.x - 1},${floatingArrow.y - 1} ${floatingArrow.x - 1},${floatingArrow.y + 1}`}
                    fill="#f97316"
                  />
                </g>
              )}
              
              {/* Country location markers */}
              {people.map((person) => (
                <g key={person.name}>
                  <circle
                    cx={person.x}
                    cy={person.y}
                    r="2"
                    fill="#22c55e"
                    stroke="#fff"
                    strokeWidth={0.5}
                    className="cursor-pointer transition-all hover:r-3"
                  />
                  <circle
                    cx={person.x}
                    cy={person.y}
                    r="4"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth={0.3}
                    opacity={0.3}
                    className="animate-ping"
                  />
                  <circle
                    cx={person.x}
                    cy={person.y}
                    r="6"
                    fill="none"
                    stroke="#f97316"
                    strokeWidth={0.2}
                    opacity={0.2}
                    className="animate-ping"
                    style={{ animationDelay: "1s" }}
                  />
                </g>
              ))}
            </svg>
          </div>

          {/* Circular profile pictures on countries */}
          {people.map((person) => (
            <div
              key={person.name}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-[100] group hover:z-[101]"
              style={{
                left: `${person.x}%`,
                top: `${person.y}%`,
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={() => {
                console.log('Hovering over:', person.name);
                setHoveredPerson(person);
                // Clear any active floating comments when hovering on a profile
                setActiveComment(null);
              }}
              onMouseLeave={() => {
                console.log('Left hover:', person.name);
                setHoveredPerson(null);
              }}
            >
              <div className="relative">
                <div className="relative">
                  <img
                    src={person.profileImage}
                    alt={person.name}
                    className="w-12 h-12 md:w-16 md:h-16 rounded-full border-4 border-white shadow-2xl object-cover group-hover:scale-110 group-hover:border-primary transition-all duration-200 hover:shadow-green-500/50"
                    onError={(e) => {
                      // Fallback to placeholder if image doesn't exist
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(person.name)}&background=22c55e&color=fff&size=64&font-size=0.4`;
                    }}
                  />
                  {/* Ring indicator */}
                  <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-40"></div>
                  <div
                    className="absolute inset-0 rounded-full border-2 border-orange-400 animate-ping opacity-30"
                    style={{ animationDelay: "0.5s" }}
                  ></div>
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 md:w-7 md:h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center text-xs border-2 border-white shadow-lg">
                  <span className="text-[10px] md:text-xs">{person.flag}</span>
                </div>
                {/* Online status indicator */}
                <div className="absolute -top-1 -left-1 w-3 h-3 md:w-4 md:h-4 rounded-full bg-green-500 border-2 border-white animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Small floating comment bubble */}
        {activeComment && (
          <div
            className="absolute z-[9998] animate-in fade-in-0 zoom-in-95 duration-500"
            style={{
              left: `${activeComment.person.x}%`,
              top: `${activeComment.person.y - 8}%`,
              transform: "translate(-50%, -100%)",
            }}
          >
            <div className="relative">
              {/* Small comment bubble */}
              <div className="bg-white/98 backdrop-blur-sm rounded-xl p-2 shadow-lg border border-green-300 max-w-[180px]">
                <div className="flex items-center gap-2 mb-1">
                  <img
                    src={activeComment.person.profileImage}
                    alt={activeComment.person.name}
                    className="w-6 h-6 rounded-full border border-green-400"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(activeComment.person.name)}&background=22c55e&color=fff&size=24`;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-xs leading-tight">
                      {activeComment.person.name}
                    </h4>
                    <p className="text-green-700 text-[10px] font-medium">
                      {activeComment.person.company}
                    </p>
                  </div>
                  <span className="text-sm">{activeComment.person.flag}</span>
                </div>

                <div className="flex items-center gap-0.5 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-orange-500 text-xs">
                      ★
                    </span>
                  ))}
                </div>

                <p className="text-gray-800 text-xs italic leading-tight">
                  "{activeComment.person.testimonial.substring(0, 60)}..."
                </p>
              </div>

              {/* Speech bubble tail */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                <div className="w-3 h-3 bg-white/98 rotate-45 border-r border-b border-green-300"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Large mouse hover info card */}
      {hoveredPerson && (
        <div className="absolute top-0 -right-2 translate-x-full w-72 md:w-80 z-[10000] animate-in fade-in-0 slide-in-from-right-2 duration-200 pointer-events-none">
          <div className="bg-white/98 backdrop-blur-xl border-2 border-green-400/70 rounded-2xl p-4 md:p-6 shadow-2xl">
            <div className="flex items-center gap-4 mb-4">
              <img
                src={hoveredPerson.profileImage}
                alt={hoveredPerson.name}
                className="w-14 h-14 md:w-18 md:h-18 rounded-xl object-cover border-3 border-green-500 shadow-lg"
                onError={(e) => {
                  // Fallback to placeholder if image doesn't exist
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(hoveredPerson.name)}&background=22c55e&color=fff&size=72`;
                }}
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-lg md:text-xl leading-tight">
                  {hoveredPerson.name}
                </h3>
                <p className="text-green-700 font-semibold text-sm md:text-base">
                  {hoveredPerson.role}
                </p>
                <p className="text-orange-600 text-sm md:text-base font-medium">
                  {hoveredPerson.company}
                </p>
                <p className="text-gray-600 text-xs md:text-sm font-medium">
                  {hoveredPerson.country}
                </p>
              </div>
              <div className="text-3xl">{hoveredPerson.flag}</div>
            </div>

            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-orange-500 text-lg">
                  ★
                </span>
              ))}
              <span className="text-green-700 text-sm ml-2 font-semibold">
                5.0 Rating
              </span>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <p className="text-gray-800 text-sm md:text-base leading-relaxed italic font-medium">
                "{hoveredPerson.testimonial}"
              </p>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Client since 2023</span>
              <span>Verified Review</span>
            </div>
          </div>
        </div>
      )}

      {/* Info overlay */}
      <div className="absolute bottom-4 left-4 z-30">
        <div className="bg-white/95 backdrop-blur-xl border border-green-400/50 rounded-xl px-4 py-3 shadow-xl">
          <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">
            Global Client Network
          </h3>
          <p className="text-gray-700 text-xs md:text-sm">
            Hover over profiles • Watch floating testimonials
          </p>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-orange-500 rounded-full animate-pulse"></div>
            <span className="text-gray-600 text-xs font-medium">
              Live worldwide
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
